import React, { FocusEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import ReactQuill from 'react-quill';
import { FormComponent, FormComponentProps } from '../../index';
import { parseError } from '../../../../services/utils';
import FormField from '../../../common/form-field';
import 'react-quill/dist/quill.snow.css';
import css from './wysiwyg.module.css';

export default function Wysiwyg(
  name: string,
  label: string = null
): FormComponent {
  return ({ ...rest }) => (
    <WysiwygComponent name={name} label={label} {...rest} />
  );
}

export function WysiwygComponent({
  name,
  label,
  parentName,
  readonly,
  required,
}: FormComponentProps & {
  name: string;
  label?: string;
  required?: string[];
  onBlur?: (
    event: FocusEvent<HTMLTextAreaElement>,
    form: UseFormReturn
  ) => void;
}) {
  const { t } = useTranslation();
  const form = useFormContext();
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
  } = form;
  const inputName = parentName ? `${parentName}.${name}` : name;
  const error = parseError(inputName, errors);
  const [content, setContent] = useState('');
  const [modules, setModules] = useState({
    toolbar: [
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image', 'video'],
      ['clean'],
    ],
  });

  useEffect(() => {
    setContent(getValues(inputName));
  }, [inputName, getValues, form]);

  useEffect(() => {
    if (readonly) setModules({ toolbar: null });
  }, [readonly]);

  const onChange = (value: any) => {
    if (!readonly) {
      setContent(value);
      setValue(inputName, value, { shouldValidate: true });
    }
  };

  return (
    <FormField
      label={label ? t(label) : t(name)}
      labelFor={inputName}
      required={required?.includes(inputName)}
      error={error ? error.message : null}
    >
      <input type="hidden" name={inputName} {...register(inputName)} />
      <div className={`${css['wysiwyg']} ${readonly ? css['readonly'] : ''}`}>
        {readonly ? (
          <div
            className="ql-editor"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : (
          <ReactQuill
            readOnly={readonly}
            modules={modules}
            value={content}
            onChange={onChange}
          />
        )}
      </div>
    </FormField>
  );
}
